import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useLanguage } from '@/contexts/LanguageContext';
import { 
  Music, Plus, FileText, Edit, Trash2, Upload, Eye, 
  Clock, Calendar, Play, Users, Loader2, ChevronRight,
  Music2, Book, Mic, Heart, Gift, X, Search, Filter, Download, Printer,
  GripVertical
} from 'lucide-react';
import { getErrorMessage } from '../../utils/errorHandler';
import SortableList from '../SortableList';
import SongImportDialog from './SongImportDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ITEM_TYPE_ICONS = {
  song: Music2,
  prayer: Heart,
  scripture: Book,
  sermon: Mic,
  welcome: Users,
  announcement: FileText,
  offering: Gift,
  activity: Play,
};

const ITEM_TYPE_COLORS = {
  song: 'bg-pink-500',
  prayer: 'bg-purple-500',
  scripture: 'bg-blue-500',
  sermon: 'bg-amber-500',
  welcome: 'bg-green-500',
  announcement: 'bg-slate-500',
  offering: 'bg-emerald-500',
  activity: 'bg-orange-500',
};

const MUSICAL_KEYS = ['C', 'C#/Db', 'D', 'D#/Eb', 'E', 'F', 'F#/Gb', 'G', 'G#/Ab', 'A', 'A#/Bb', 'B', 
                      'Cm', 'C#m/Dbm', 'Dm', 'D#m/Ebm', 'Em', 'Fm', 'F#m/Gbm', 'Gm', 'G#m/Abm', 'Am', 'A#m/Bbm', 'Bm'];

const MusicTab = ({ communityId, userRole, communityType = 'church' }) => {
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState('songs');
  const [songs, setSongs] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [templates, setTemplates] = useState({});
  const [loading, setLoading] = useState(true);
  
  // Context-aware labels
  const isSchoolBand = communityType === 'education';
  const labels = {
    tabSongs: isSchoolBand ? t('sheet_music') : t('song_charts'),
    tabSchedules: isSchoolBand ? t('rehearsals') : t('music_schedules'),
    addSong: isSchoolBand ? t('add_sheet_music') : t('add_song_chart'),
    addSchedule: isSchoolBand ? t('new_rehearsal') : t('new_schedule'),
    songTitle: isSchoolBand ? t('piece_title') : t('song_title_label'),
    artist: isSchoolBand ? t('composer') : t('artist'),
    noSongs: isSchoolBand ? t('no_sheet_music') : t('no_song_charts'),
    noSongsDesc: isSchoolBand ? t('no_sheet_music_desc') : t('no_song_charts_desc'),
    noSchedules: isSchoolBand ? t('no_rehearsals') : t('no_worship_schedules'),
    noSchedulesDesc: isSchoolBand ? t('no_rehearsals_desc') : t('no_schedules_desc'),
    footerText: isSchoolBand ? t('school_band_footer') : t('music_ministry_footer')
  };
  
  // Search and filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [filterKey, setFilterKey] = useState('all');
  const [filterTag, setFilterTag] = useState('all');
  
  // Dialog states
  const [songDialogOpen, setSongDialogOpen] = useState(false);
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [viewSongDialog, setViewSongDialog] = useState(null);
  const [viewScheduleDialog, setViewScheduleDialog] = useState(null);
  const [editingSong, setEditingSong] = useState(null);
  
  // Form states
  const [newSong, setNewSong] = useState({
    title: '', artist: '', key: '', tempo: '', lyrics: '', chords: '', notes: '', tags: []
  });
  const [newSchedule, setNewSchedule] = useState({
    title: '', service_date: '', service_time: '10:00', items: [], template: ''
  });
  const [tagInput, setTagInput] = useState('');

  // Roles that can edit - church: admin, pastor, worship_leader, music; education: admin, teacher, assistant
  const canEdit = isSchoolBand 
    ? ['admin', 'teacher', 'assistant'].includes(userRole)
    : ['admin', 'pastor', 'worship_leader', 'music'].includes(userRole);

  // Get all unique tags from songs
  const allTags = useMemo(() => {
    const tags = new Set();
    songs.forEach(song => {
      song.tags?.forEach(tag => tags.add(tag));
    });
    return Array.from(tags).sort();
  }, [songs]);

  // Get all unique keys from songs
  const allKeys = useMemo(() => {
    const keys = new Set();
    songs.forEach(song => {
      if (song.key) keys.add(song.key);
    });
    return Array.from(keys).sort();
  }, [songs]);

  // Filtered songs based on search and filters
  const filteredSongs = useMemo(() => {
    return songs.filter(song => {
      // Search filter
      const searchLower = searchQuery.toLowerCase();
      const matchesSearch = !searchQuery || 
        song.title?.toLowerCase().includes(searchLower) ||
        song.artist?.toLowerCase().includes(searchLower) ||
        song.lyrics?.toLowerCase().includes(searchLower) ||
        song.tags?.some(tag => tag.toLowerCase().includes(searchLower));
      
      // Key filter
      const matchesKey = filterKey === 'all' || song.key === filterKey;
      
      // Tag filter
      const matchesTag = filterTag === 'all' || song.tags?.includes(filterTag);
      
      return matchesSearch && matchesKey && matchesTag;
    });
  }, [songs, searchQuery, filterKey, filterTag]);

  // Export schedule to PDF
  const exportScheduleToPDF = (schedule) => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      toast.error('Please allow popups to export PDF');
      return;
    }

    const totalDuration = schedule.items?.reduce((sum, item) => sum + (item.duration_minutes || 0), 0) || 0;
    const serviceDate = new Date(schedule.service_date).toLocaleDateString('en-US', { 
      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' 
    });

    const itemTypeLabels = {
      song: '🎵 Song',
      prayer: '🙏 Prayer',
      scripture: '📖 Scripture',
      sermon: '🎤 Sermon',
      welcome: '👋 Welcome',
      announcement: '📢 Announcement',
      offering: '💝 Offering',
      activity: '🎯 Activity'
    };

    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>${schedule.title} - Worship Schedule</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { 
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            padding: 40px;
            max-width: 800px;
            margin: 0 auto;
            color: #1a1a1a;
          }
          .header { 
            text-align: center; 
            margin-bottom: 30px;
            padding-bottom: 20px;
            border-bottom: 2px solid #e5e5e5;
          }
          .header h1 { 
            font-size: 28px; 
            margin-bottom: 8px;
            color: #1a1a1a;
          }
          .header .date { 
            font-size: 16px; 
            color: #666;
          }
          .header .time {
            font-size: 14px;
            color: #888;
            margin-top: 4px;
          }
          .summary {
            display: flex;
            justify-content: space-between;
            background: #f8f8f8;
            padding: 15px 20px;
            border-radius: 8px;
            margin-bottom: 25px;
            font-size: 14px;
          }
          .summary span { color: #666; }
          .summary strong { color: #1a1a1a; }
          .items { margin-top: 20px; }
          .item { 
            display: flex;
            align-items: flex-start;
            padding: 15px 0;
            border-bottom: 1px solid #eee;
          }
          .item:last-child { border-bottom: none; }
          .item-number {
            width: 30px;
            height: 30px;
            background: #f0f0f0;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-weight: 600;
            font-size: 14px;
            margin-right: 15px;
            flex-shrink: 0;
          }
          .item-content { flex: 1; }
          .item-title { 
            font-weight: 600; 
            font-size: 16px;
            margin-bottom: 4px;
          }
          .item-meta {
            display: flex;
            gap: 15px;
            font-size: 13px;
            color: #666;
          }
          .item-type {
            background: #f0f0f0;
            padding: 2px 8px;
            border-radius: 4px;
            font-size: 12px;
          }
          .item-duration {
            text-align: right;
            min-width: 60px;
            font-size: 14px;
            color: #888;
          }
          .item-notes {
            font-size: 13px;
            color: #888;
            margin-top: 6px;
            font-style: italic;
          }
          .item-assigned {
            font-size: 13px;
            color: #666;
            margin-top: 4px;
          }
          .footer {
            margin-top: 30px;
            padding-top: 20px;
            border-top: 2px solid #e5e5e5;
            text-align: center;
            font-size: 12px;
            color: #999;
          }
          @media print {
            body { padding: 20px; }
            .no-print { display: none; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>${schedule.title}</h1>
          <div class="date">${serviceDate}</div>
          <div class="time">Service Time: ${schedule.service_time}</div>
        </div>
        
        <div class="summary">
          <span>Total Items: <strong>${schedule.items?.length || 0}</strong></span>
          <span>Total Duration: <strong>${totalDuration} minutes</strong></span>
          <span>Status: <strong>${schedule.status || 'Draft'}</strong></span>
        </div>

        <div class="items">
          ${schedule.items?.map((item, index) => `
            <div class="item">
              <div class="item-number">${index + 1}</div>
              <div class="item-content">
                <div class="item-title">${item.title}</div>
                <div class="item-meta">
                  <span class="item-type">${itemTypeLabels[item.type] || item.type}</span>
                  ${item.assigned_to?.length ? `<span>Assigned: ${item.assigned_to.join(', ')}</span>` : ''}
                </div>
                ${item.notes ? `<div class="item-notes">${item.notes}</div>` : ''}
              </div>
              <div class="item-duration">${item.duration_minutes} min</div>
            </div>
          `).join('') || '<p>No items in this schedule</p>'}
        </div>

        <div class="footer">
          Generated from ${labels.footerText} • ${new Date().toLocaleDateString()}
        </div>

        <script>
          window.onload = function() { window.print(); }
        </script>
      </body>
      </html>
    `;

    printWindow.document.write(html);
    printWindow.document.close();
  };

  // Export song chart to PDF
  const exportSongToPDF = (song) => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      toast.error('Please allow popups to export PDF');
      return;
    }

    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>${song.title} - Song Chart</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { 
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            padding: 40px;
            max-width: 800px;
            margin: 0 auto;
            color: #1a1a1a;
          }
          .header { 
            text-align: center; 
            margin-bottom: 30px;
            padding-bottom: 20px;
            border-bottom: 2px solid #e5e5e5;
          }
          .header h1 { 
            font-size: 32px; 
            margin-bottom: 8px;
          }
          .header .artist { 
            font-size: 18px; 
            color: #666;
            margin-bottom: 10px;
          }
          .header .meta {
            display: flex;
            justify-content: center;
            gap: 20px;
            font-size: 14px;
            color: #888;
          }
          .header .meta span {
            background: #f0f0f0;
            padding: 4px 12px;
            border-radius: 4px;
          }
          .section {
            margin-bottom: 30px;
          }
          .section h2 {
            font-size: 16px;
            color: #666;
            margin-bottom: 12px;
            text-transform: uppercase;
            letter-spacing: 1px;
          }
          .section pre {
            font-family: 'Courier New', monospace;
            font-size: 14px;
            line-height: 1.8;
            white-space: pre-wrap;
            background: #f8f8f8;
            padding: 20px;
            border-radius: 8px;
          }
          .chords pre {
            font-weight: 600;
            color: #2563eb;
          }
          .notes {
            font-size: 14px;
            color: #666;
            font-style: italic;
            background: #fffbeb;
            padding: 15px;
            border-radius: 8px;
            border-left: 4px solid #f59e0b;
          }
          .tags {
            display: flex;
            gap: 8px;
            flex-wrap: wrap;
            margin-top: 20px;
          }
          .tag {
            background: #e5e5e5;
            padding: 4px 10px;
            border-radius: 4px;
            font-size: 12px;
          }
          .footer {
            margin-top: 30px;
            padding-top: 20px;
            border-top: 2px solid #e5e5e5;
            text-align: center;
            font-size: 12px;
            color: #999;
          }
          @media print {
            body { padding: 20px; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>${song.title}</h1>
          ${song.artist ? `<div class="artist">by ${song.artist}</div>` : ''}
          <div class="meta">
            ${song.key ? `<span>Key: ${song.key}</span>` : ''}
            ${song.tempo ? `<span>Tempo: ${song.tempo} BPM</span>` : ''}
          </div>
        </div>

        ${song.chords ? `
          <div class="section chords">
            <h2>Chords</h2>
            <pre>${song.chords}</pre>
          </div>
        ` : ''}

        ${song.lyrics ? `
          <div class="section lyrics">
            <h2>Lyrics</h2>
            <pre>${song.lyrics}</pre>
          </div>
        ` : ''}

        ${song.notes ? `
          <div class="section">
            <h2>Notes</h2>
            <div class="notes">${song.notes}</div>
          </div>
        ` : ''}

        ${song.tags?.length ? `
          <div class="tags">
            ${song.tags.map(tag => `<span class="tag">${tag}</span>`).join('')}
          </div>
        ` : ''}

        <div class="footer">
          Generated from ${labels.footerText} • ${new Date().toLocaleDateString()}
        </div>

        <script>
          window.onload = function() { window.print(); }
        </script>
      </body>
      </html>
    `;

    printWindow.document.write(html);
    printWindow.document.close();
  };

  useEffect(() => {
    fetchData();
  }, [communityId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [songsRes, schedulesRes, templatesRes] = await Promise.all([
        axios.get(`${API}/communities/${communityId}/songs`, { withCredentials: true }),
        axios.get(`${API}/communities/${communityId}/schedules`, { withCredentials: true }),
        axios.get(`${API}/schedule-templates`, { withCredentials: true })
      ]);
      setSongs(songsRes.data.charts || []);
      setSchedules(schedulesRes.data.schedules || []);
      setTemplates(templatesRes.data.templates || {});
    } catch (error) {
      console.error('Failed to fetch music data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Song handlers
  const handleCreateSong = async () => {
    try {
      const payload = {
        ...newSong,
        tempo: newSong.tempo ? parseInt(newSong.tempo) : null
      };
      
      if (editingSong) {
        await axios.put(`${API}/communities/${communityId}/songs/${editingSong.chart_id}`, payload, { withCredentials: true });
        toast.success(isSchoolBand ? 'Sheet music updated!' : 'Song chart updated!');
      } else {
        await axios.post(`${API}/communities/${communityId}/songs`, payload, { withCredentials: true });
        toast.success(isSchoolBand ? 'Sheet music added!' : 'Song chart created!');
      }
      
      setSongDialogOpen(false);
      setEditingSong(null);
      setNewSong({ title: '', artist: '', key: '', tempo: '', lyrics: '', chords: '', notes: '', tags: [] });
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to save'));
    }
  };

  const handleDeleteSong = async (chartId) => {
    if (!confirm(isSchoolBand ? 'Are you sure you want to delete this sheet music?' : 'Are you sure you want to delete this song chart?')) return;
    
    try {
      await axios.delete(`${API}/communities/${communityId}/songs/${chartId}`, { withCredentials: true });
      toast.success(isSchoolBand ? 'Sheet music deleted' : 'Song chart deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete song');
    }
  };

  const handleEditSong = (song) => {
    setEditingSong(song);
    setNewSong({
      title: song.title || '',
      artist: song.artist || '',
      key: song.key || '',
      tempo: song.tempo?.toString() || '',
      lyrics: song.lyrics || '',
      chords: song.chords || '',
      notes: song.notes || '',
      tags: song.tags || []
    });
    setSongDialogOpen(true);
  };

  const addTag = () => {
    if (tagInput.trim() && !newSong.tags.includes(tagInput.trim())) {
      setNewSong({ ...newSong, tags: [...newSong.tags, tagInput.trim()] });
      setTagInput('');
    }
  };

  const removeTag = (tag) => {
    setNewSong({ ...newSong, tags: newSong.tags.filter(t => t !== tag) });
  };

  // Schedule handlers
  const handleCreateSchedule = async () => {
    try {
      if (newSchedule.template) {
        await axios.post(
          `${API}/communities/${communityId}/schedules/from-template?template_key=${newSchedule.template}&title=${encodeURIComponent(newSchedule.title)}&service_date=${newSchedule.service_date}&service_time=${newSchedule.service_time}`,
          {},
          { withCredentials: true }
        );
      } else {
        await axios.post(`${API}/communities/${communityId}/schedules`, newSchedule, { withCredentials: true });
      }
      toast.success('Worship schedule created!');
      setScheduleDialogOpen(false);
      setNewSchedule({ title: '', service_date: '', service_time: '10:00', items: [], template: '' });
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to create schedule'));
    }
  };

  const handleDeleteSchedule = async (scheduleId) => {
    if (!confirm('Are you sure you want to delete this schedule?')) return;
    
    try {
      await axios.delete(`${API}/communities/${communityId}/schedules/${scheduleId}`, { withCredentials: true });
      toast.success('Schedule deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete schedule');
    }
  };

  const handlePublishSchedule = async (scheduleId) => {
    try {
      await axios.post(`${API}/communities/${communityId}/schedules/${scheduleId}/publish`, {}, { withCredentials: true });
      toast.success('Schedule published!');
      fetchData();
    } catch (error) {
      toast.error('Failed to publish schedule');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-pink-500" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="flex items-center justify-between mb-4">
          <TabsList>
            <TabsTrigger value="songs" className="gap-2">
              <Music className="w-4 h-4" />
              {labels.tabSongs} ({songs.length})
            </TabsTrigger>
            <TabsTrigger value="schedules" className="gap-2">
              <Calendar className="w-4 h-4" />
              {labels.tabSchedules} ({schedules.length})
            </TabsTrigger>
          </TabsList>
          
          {canEdit && (
            <div className="flex gap-2">
              {activeTab === 'songs' && (
                <>
                <SongImportDialog
                  communityId={communityId}
                  t={t}
                  onImported={() => { fetchData(); }}
                />
                <Dialog open={songDialogOpen} onOpenChange={(open) => {
                  setSongDialogOpen(open);
                  if (!open) {
                    setEditingSong(null);
                    setNewSong({ title: '', artist: '', key: '', tempo: '', lyrics: '', chords: '', notes: '', tags: [] });
                  }
                }}>
                  <DialogTrigger asChild>
                    <Button size="sm" className="gap-2 bg-pink-600 hover:bg-pink-700">
                      <Plus className="w-4 h-4" />
                      {labels.addSong}
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>{editingSong ? (isSchoolBand ? 'Edit Sheet Music' : 'Edit Song Chart') : (isSchoolBand ? 'Add Sheet Music' : 'Add Song Chart')}</DialogTitle>
                      <DialogDescription>
                        {isSchoolBand ? 'Add music details and notes for your band' : 'Add song details, lyrics, and chords for your worship team'}
                      </DialogDescription>
                    </DialogHeader>
                    <div className="grid gap-4 py-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>{isSchoolBand ? 'Piece Title' : 'Title'} *</Label>
                          <Input 
                            value={newSong.title} 
                            onChange={(e) => setNewSong({...newSong, title: e.target.value})}
                            placeholder={isSchoolBand ? 'Piece title' : 'Song title'}
                          />
                        </div>
                        <div>
                          <Label>{isSchoolBand ? 'Composer' : 'Artist'}</Label>
                          <Input 
                            value={newSong.artist} 
                            onChange={(e) => setNewSong({...newSong, artist: e.target.value})}
                            placeholder={isSchoolBand ? 'Composer name' : 'Artist or band'}
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Key</Label>
                          <Select value={newSong.key} onValueChange={(v) => setNewSong({...newSong, key: v})}>
                            <SelectTrigger>
                              <SelectValue placeholder="Select key" />
                            </SelectTrigger>
                            <SelectContent>
                              {MUSICAL_KEYS.map(key => (
                                <SelectItem key={key} value={key}>{key}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label>Tempo (BPM)</Label>
                          <Input 
                            type="number"
                            value={newSong.tempo} 
                            onChange={(e) => setNewSong({...newSong, tempo: e.target.value})}
                            placeholder="e.g., 120"
                          />
                        </div>
                      </div>
                      <div>
                        <Label>Lyrics</Label>
                        <Textarea 
                          value={newSong.lyrics} 
                          onChange={(e) => setNewSong({...newSong, lyrics: e.target.value})}
                          placeholder="Enter song lyrics..."
                          rows={6}
                        />
                      </div>
                      <div>
                        <Label>Chords</Label>
                        <Textarea 
                          value={newSong.chords} 
                          onChange={(e) => setNewSong({...newSong, chords: e.target.value})}
                          placeholder="Enter chord progression or chart..."
                          rows={4}
                        />
                      </div>
                      <div>
                        <Label>Notes</Label>
                        <Textarea 
                          value={newSong.notes} 
                          onChange={(e) => setNewSong({...newSong, notes: e.target.value})}
                          placeholder="Any additional notes for the team..."
                          rows={2}
                        />
                      </div>
                      <div>
                        <Label>Tags</Label>
                        <div className="flex gap-2 mb-2 flex-wrap">
                          {newSong.tags.map(tag => (
                            <Badge key={tag} variant="secondary" className="gap-1">
                              {tag}
                              <X className="w-3 h-3 cursor-pointer" onClick={() => removeTag(tag)} />
                            </Badge>
                          ))}
                        </div>
                        <div className="flex gap-2">
                          <Input 
                            value={tagInput}
                            onChange={(e) => setTagInput(e.target.value)}
                            placeholder="Add tag (e.g., worship, fast, slow)"
                            onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                          />
                          <Button type="button" variant="outline" onClick={addTag}>Add</Button>
                        </div>
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setSongDialogOpen(false)}>Cancel</Button>
                      <Button onClick={handleCreateSong} disabled={!newSong.title}>
                        {editingSong ? 'Update Song' : 'Add Song'}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
                </>
              )}
              
              {activeTab === 'schedules' && (
                <Dialog open={scheduleDialogOpen} onOpenChange={setScheduleDialogOpen}>
                  <DialogTrigger asChild>
                    <Button size="sm" className="gap-2 bg-pink-600 hover:bg-pink-700">
                      <Plus className="w-4 h-4" />
                      {labels.addSchedule}
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>{isSchoolBand ? 'Create Rehearsal Plan' : 'Create Worship Schedule'}</DialogTitle>
                      <DialogDescription>
                        {isSchoolBand ? 'Create a new rehearsal plan or use a template' : 'Create a new worship schedule or use a template'}
                      </DialogDescription>
                    </DialogHeader>
                    <div className="grid gap-4 py-4">
                      <div>
                        <Label>Title *</Label>
                        <Input 
                          value={newSchedule.title} 
                          onChange={(e) => setNewSchedule({...newSchedule, title: e.target.value})}
                          placeholder={isSchoolBand ? 'e.g., Band Rehearsal - March 10th' : 'e.g., Sunday Service - March 10th'}
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>{isSchoolBand ? 'Rehearsal Date *' : 'Service Date *'}</Label>
                          <Input 
                            type="date"
                            value={newSchedule.service_date} 
                            onChange={(e) => setNewSchedule({...newSchedule, service_date: e.target.value})}
                          />
                        </div>
                        <div>
                          <Label>{isSchoolBand ? 'Rehearsal Time' : 'Service Time'}</Label>
                          <Input 
                            type="time"
                            value={newSchedule.service_time} 
                            onChange={(e) => setNewSchedule({...newSchedule, service_time: e.target.value})}
                          />
                        </div>
                      </div>
                      <div>
                        <Label>Use Template</Label>
                        <Select value={newSchedule.template} onValueChange={(v) => setNewSchedule({...newSchedule, template: v === 'none' ? '' : v})}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select a template (optional)" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">No template</SelectItem>
                            {Object.entries(templates).map(([key, template]) => (
                              <SelectItem key={key} value={key}>{template.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setScheduleDialogOpen(false)}>Cancel</Button>
                      <Button onClick={handleCreateSchedule} disabled={!newSchedule.title || !newSchedule.service_date}>
                        Create Schedule
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              )}
            </div>
          )}
        </div>

        {/* Songs Tab */}
        <TabsContent value="songs">
          {/* Search and Filter Bar */}
          {songs.length > 0 && (
            <div className="mb-4 p-4 bg-slate-50 dark:bg-slate-900 rounded-lg">
              <div className="flex flex-wrap items-center gap-3">
                <div className="relative flex-1 min-w-[200px]">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder={isSchoolBand ? "Search by title, composer..." : "Search songs by title, artist, or lyrics..."}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <Select value={filterKey} onValueChange={setFilterKey}>
                  <SelectTrigger className="w-[140px]">
                    <SelectValue placeholder="Filter by key" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Keys</SelectItem>
                    {allKeys.map(key => (
                      <SelectItem key={key} value={key}>{key}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={filterTag} onValueChange={setFilterTag}>
                  <SelectTrigger className="w-[150px]">
                    <SelectValue placeholder="Filter by tag" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Tags</SelectItem>
                    {allTags.map(tag => (
                      <SelectItem key={tag} value={tag}>{tag}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {(searchQuery || filterKey !== 'all' || filterTag !== 'all') && (
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => {
                      setSearchQuery('');
                      setFilterKey('all');
                      setFilterTag('all');
                    }}
                  >
                    <X className="w-4 h-4 mr-1" />
                    Clear
                  </Button>
                )}
              </div>
              {searchQuery || filterKey !== 'all' || filterTag !== 'all' ? (
                <p className="text-sm text-slate-500 mt-2">
                  Showing {filteredSongs.length} of {songs.length} songs
                </p>
              ) : null}
            </div>
          )}

          {songs.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Music className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                <p className="text-slate-500">{labels.noSongs}</p>
                {canEdit && <p className="text-sm text-slate-400">{labels.noSongsDesc}</p>}
              </CardContent>
            </Card>
          ) : filteredSongs.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Search className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                <p className="text-slate-500">No results match your search</p>
                <p className="text-sm text-slate-400">Try adjusting your filters or search terms</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-3">
              {filteredSongs.map(song => (
                <Card key={song.chart_id} className="hover:border-pink-300 transition-colors">
                  <CardContent className="py-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-lg bg-pink-100 dark:bg-pink-900/30 flex items-center justify-center">
                          <Music2 className="w-5 h-5 text-pink-600" />
                        </div>
                        <div>
                          <h4 className="font-medium">{song.title}</h4>
                          <div className="flex items-center gap-3 text-sm text-slate-500">
                            {song.artist && <span>{song.artist}</span>}
                            {song.key && <Badge variant="outline" className="text-xs">{song.key}</Badge>}
                            {song.tempo && <span>{song.tempo} BPM</span>}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {song.tags?.slice(0, 2).map(tag => (
                          <Badge key={tag} variant="secondary" className="text-xs">{tag}</Badge>
                        ))}
                        {song.tags?.length > 2 && (
                          <Badge variant="secondary" className="text-xs">+{song.tags.length - 2}</Badge>
                        )}
                        <Button variant="ghost" size="icon" onClick={() => setViewSongDialog(song)} title="View">
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => exportSongToPDF(song)} title="Print/Export PDF">
                          <Printer className="w-4 h-4" />
                        </Button>
                        {canEdit && (
                          <>
                            <Button variant="ghost" size="icon" onClick={() => handleEditSong(song)} title="Edit">
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="icon" className="text-red-500 hover:text-red-600" onClick={() => handleDeleteSong(song.chart_id)} title="Delete">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Schedules Tab */}
        <TabsContent value="schedules">
          {schedules.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Calendar className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                <p className="text-slate-500">No worship schedules yet</p>
                {canEdit && <p className="text-sm text-slate-400">Create a schedule to plan your worship services</p>}
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-3">
              {schedules.map(schedule => (
                <Card key={schedule.schedule_id} className="hover:border-pink-300 transition-colors">
                  <CardContent className="py-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-lg bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                          <Calendar className="w-5 h-5 text-amber-600" />
                        </div>
                        <div>
                          <h4 className="font-medium">{schedule.title}</h4>
                          <div className="flex items-center gap-3 text-sm text-slate-500">
                            <span>{new Date(schedule.service_date).toLocaleDateString()}</span>
                            <span>{schedule.service_time}</span>
                            <Badge variant={schedule.status === 'published' ? 'default' : 'secondary'}>
                              {schedule.status}
                            </Badge>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-slate-400">{schedule.items?.length || 0} items</span>
                        <Button variant="ghost" size="icon" onClick={() => setViewScheduleDialog(schedule)} title="View">
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => exportScheduleToPDF(schedule)} title="Print/Export PDF">
                          <Printer className="w-4 h-4" />
                        </Button>
                        {canEdit && schedule.status === 'draft' && (
                          <Button variant="outline" size="sm" onClick={() => handlePublishSchedule(schedule.schedule_id)}>
                            Publish
                          </Button>
                        )}
                        {canEdit && (
                          <Button variant="ghost" size="icon" className="text-red-500 hover:text-red-600" onClick={() => handleDeleteSchedule(schedule.schedule_id)} title="Delete">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* View Song Dialog */}
      <Dialog open={!!viewSongDialog} onOpenChange={() => setViewSongDialog(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Music2 className="w-5 h-5 text-pink-600" />
              {viewSongDialog?.title}
            </DialogTitle>
            <DialogDescription>
              {viewSongDialog?.artist && `by ${viewSongDialog.artist}`}
              {viewSongDialog?.key && ` • Key: ${viewSongDialog.key}`}
              {viewSongDialog?.tempo && ` • ${viewSongDialog.tempo} BPM`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {viewSongDialog?.lyrics && (
              <div>
                <h4 className="font-semibold mb-2">Lyrics</h4>
                <pre className="whitespace-pre-wrap text-sm bg-slate-50 dark:bg-slate-900 p-4 rounded-lg">
                  {viewSongDialog.lyrics}
                </pre>
              </div>
            )}
            {viewSongDialog?.chords && (
              <div>
                <h4 className="font-semibold mb-2">Chords</h4>
                <pre className="whitespace-pre-wrap text-sm bg-slate-50 dark:bg-slate-900 p-4 rounded-lg font-mono">
                  {viewSongDialog.chords}
                </pre>
              </div>
            )}
            {viewSongDialog?.notes && (
              <div>
                <h4 className="font-semibold mb-2">Notes</h4>
                <p className="text-sm text-slate-600 dark:text-slate-400">{viewSongDialog.notes}</p>
              </div>
            )}
            {viewSongDialog?.tags?.length > 0 && (
              <div className="flex gap-2 flex-wrap">
                {viewSongDialog.tags.map(tag => (
                  <Badge key={tag} variant="secondary">{tag}</Badge>
                ))}
              </div>
            )}
            <div className="pt-4 border-t">
              <Button onClick={() => exportSongToPDF(viewSongDialog)} className="gap-2">
                <Printer className="w-4 h-4" />
                Print / Export PDF
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* View Schedule Dialog */}
      <Dialog open={!!viewScheduleDialog} onOpenChange={() => setViewScheduleDialog(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-amber-600" />
              {viewScheduleDialog?.title}
            </DialogTitle>
            <DialogDescription>
              {viewScheduleDialog?.service_date && new Date(viewScheduleDialog.service_date).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
              {viewScheduleDialog?.service_time && ` at ${viewScheduleDialog.service_time}`}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <h4 className="font-semibold mb-3">Service Order</h4>
            <SortableList
              items={viewScheduleDialog?.items || []}
              getId={(item) => item.item_id || `idx-${(viewScheduleDialog?.items || []).indexOf(item)}`}
              testId="schedule-items-sortable"
              onReorder={async (newItems) => {
                const optimistic = { ...viewScheduleDialog, items: newItems };
                setViewScheduleDialog(optimistic);
                try {
                  const token = localStorage.getItem('token');
                  await axios.post(
                    `${API}/music/communities/${communityId}/schedules/${viewScheduleDialog.schedule_id}/reorder`,
                    { item_ids: newItems.map((i) => i.item_id).filter(Boolean) },
                    { headers: { Authorization: `Bearer ${token}` } }
                  );
                  toast.success(t('order_saved') || 'Order saved');
                  // Refresh schedules in background
                  setSchedules((sc) => sc.map((s) => s.schedule_id === optimistic.schedule_id ? optimistic : s));
                } catch (err) {
                  toast.error(getErrorMessage(err, t('reorder_failed') || 'Failed to save order'));
                  setViewScheduleDialog(viewScheduleDialog);
                }
              }}
              renderItem={(item, isDragging, dragHandleProps) => {
                const Icon = ITEM_TYPE_ICONS[item.type] || FileText;
                const colorClass = ITEM_TYPE_COLORS[item.type] || 'bg-slate-500';
                return (
                  <div className={`flex items-center gap-3 p-3 mb-2 bg-slate-50 dark:bg-slate-900 rounded-lg ${isDragging ? 'shadow-lg ring-2 ring-amber-400/50' : ''}`}>
                    <button
                      {...dragHandleProps}
                      className="cursor-grab active:cursor-grabbing text-slate-400 hover:text-slate-700 touch-none"
                      aria-label="Drag to reorder"
                      data-testid={`drag-handle-${item.item_id}`}
                    >
                      <GripVertical className="w-4 h-4" />
                    </button>
                    <div className={`w-8 h-8 rounded-lg ${colorClass} flex items-center justify-center flex-shrink-0`}>
                      <Icon className="w-4 h-4 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium">{item.title}</p>
                      <div className="flex items-center gap-2 text-sm text-slate-500">
                        <Clock className="w-3 h-3" />
                        <span>{item.duration_minutes} min</span>
                        {item.assigned_to?.length > 0 && (
                          <>
                            <span>•</span>
                            <Users className="w-3 h-3" />
                            <span>{item.assigned_to.join(', ')}</span>
                          </>
                        )}
                      </div>
                      {item.notes && <p className="text-xs text-slate-400 mt-1">{item.notes}</p>}
                    </div>
                    <Badge variant="outline" className="capitalize flex-shrink-0">{item.type}</Badge>
                  </div>
                );
              }}
            />
            {viewScheduleDialog?.items?.length > 0 && (
              <div className="mt-4 pt-4 border-t flex justify-between text-sm text-slate-500">
                <span>Total items: {viewScheduleDialog.items.length}</span>
                <span>Total duration: {viewScheduleDialog.items.reduce((sum, item) => sum + (item.duration_minutes || 0), 0)} minutes</span>
              </div>
            )}
            <div className="mt-4 pt-4 border-t">
              <Button onClick={() => exportScheduleToPDF(viewScheduleDialog)} className="gap-2">
                <Printer className="w-4 h-4" />
                Print / Export PDF
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MusicTab;
